import {find} from 'lodash';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {matchPath} from 'react-router';
import {Switch, useLocation} from 'react-router-dom';

import {AuthLayoutRouteContext} from 'context';
import {useIsChanged} from 'hooks';
import {AuthLayout, AuthLayoutProps} from 'layouts';
import {ROUTE_CONFIG} from 'utils/route-config';
import {PrivateRoute} from 'utils/routes';

import {AboutMePage} from './About';
import {Account} from './Account';
import {Activity} from './Activity';
import {Content} from './Content';
import {Online} from './OnlineSessions';

export const AUTH_LAYOUT_PATHS = [
    ROUTE_CONFIG.ONLINE_SESSIONS.SELF.path,
    ROUTE_CONFIG.ACTIVITY.SELF.path,
    ROUTE_CONFIG.ACCOUNT.SELF.path,
    ROUTE_CONFIG.CONTENT.SELF.path,
    ROUTE_CONFIG.ABOUT.ME.path,
];

export const AuthLayoutRoute: React.FC = () => {
    const {pathname} = useLocation();
    const currentPath = useMemo(() => find(AUTH_LAYOUT_PATHS, (path) => !!matchPath(pathname, {path})), [pathname]);

    const [authLayoutProps, setAuthLayoutProps] = useState<{[path: string]: Partial<AuthLayoutProps>}>({});

    const setRouteAuthLayoutProps = useCallback((props: Partial<AuthLayoutProps>) => {
        setAuthLayoutProps((prev) => ({...prev, [currentPath]: props}));
    }, [currentPath]);

    const isCurrentPathChanged = useIsChanged(currentPath);

    useEffect(() => {
        if (isCurrentPathChanged) {
            // reset all except current
            setAuthLayoutProps((prev) => ({[currentPath]: prev[currentPath]}));
        }
    }, [currentPath, isCurrentPathChanged]);

    return (
        <AuthLayoutRouteContext.Provider value={{setAuthLayoutProps: setRouteAuthLayoutProps}}>
            <AuthLayout {...authLayoutProps[currentPath]}>
                <Switch>
                    <PrivateRoute key='online' {...ROUTE_CONFIG.ONLINE_SESSIONS.SELF} component={Online} />
                    <PrivateRoute key='activity' {...ROUTE_CONFIG.ACTIVITY.SELF} component={Activity} />
                    <PrivateRoute key='account' {...ROUTE_CONFIG.ACCOUNT.SELF} component={Account} />
                    <PrivateRoute key='content' {...ROUTE_CONFIG.CONTENT.SELF} component={Content} />
                    <PrivateRoute key='about' {...ROUTE_CONFIG.ABOUT.ME} component={AboutMePage} />
                </Switch>
            </AuthLayout>
        </AuthLayoutRouteContext.Provider>
    );
};
